import { useEffect, useMemo, useRef, useState } from 'react';
import { classNames } from 'primereact/utils';
import { getBundleApi } from '@/api';
import { BundleType, selectSharedDataBundleId, useAppSelector } from '@/store';
import { useBundleInteraction, useBundleInteractionExtractTo } from '../hooks';
import { Button, CheckCircleIcon, Toast } from '@repo/atomic-components';
import { useAccessControl } from '@/hooks';
import { AccessControlAction } from '@/constants';
import { SplittedButton } from './splittedButton';
import { ExtractToPopup } from './ExtractToPopup';

const buttonCommonPt = {
  root: { className: 'w-full justify-center disabled:opacity-100 !h-[32px] items-center shadow-sm' },
  icon: { className: 'text-[12px]' },
  label: { className: 'flex-none' },
};

export const BundleInteractionButton = () => {
  const bundleId = useAppSelector(selectSharedDataBundleId);

  const selectBundle = useMemo(() => getBundleApi.endpoints.getBundle.select({ bundleId: bundleId || '' }), [bundleId]);

  const { isLoading: isBundleLoading } = useAppSelector(selectBundle);

  const isEditBundles = useAccessControl(AccessControlAction.EDIT_BUNDLES);

  const {
    activeBundleType,
    canExtract,
    extractablePageCount,
    removablePageCount,
    isAddToLightBundleLoading,
    isRemoveFromLightBundleLoading,
    toastRef,
    handleExtract,
    handleRemove,
  } = useBundleInteraction();

  const {
    isExtractToLoading,
    isExtractToSuccess,
    categoriesVirtualList,
    showCategoriesPopup,
    setExtractedDate,
    setShowCategoriesPopup,
    showNewCategoryAutocomplete,
    setShowNewCategoryAutocomplete,
    handleClickNewCategory,
    handleClickCancelNewCategory,
    handleClickSubmitNewCategory,
  } = useBundleInteractionExtractTo();

  const isExtracting = isAddToLightBundleLoading || isExtractToLoading;
  const [showSuccess, setShowSuccess] = useState(false);
  const wasExtractToLoading = useRef(false);
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  const showSuccessTemporarily = () => {
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
    }
    setShowSuccess(true);
    successTimeoutRef.current = setTimeout(() => setShowSuccess(false), 1800);
  };

  useEffect(() => {
    if (isExtractToLoading) {
      wasExtractToLoading.current = true;
    } else if (wasExtractToLoading.current) {
      wasExtractToLoading.current = false;
      // Loading also ends when the extract fails or is refused
      if (isExtractToSuccess) {
        showSuccessTemporarily();
      }
    }
  }, [isExtractToLoading, isExtractToSuccess]);

  const handleExtractWithSuccess = async () => {
    const success = await handleExtract();
    if (success) {
      showSuccessTemporarily();
    }
  };

  const handleClickExtractTo = () => {
    setShowCategoriesPopup((prev: boolean) => !prev);
  };

  const handleNewCategoryCreated = () => {
    // Just close the autocomplete, don't extract yet
    setShowNewCategoryAutocomplete(false);
  };

  const label = useMemo(() => {
    if (activeBundleType === BundleType.FULL) {
      if (isAddToLightBundleLoading || isExtractToLoading) {
        return 'Extracting...';
      } else if (canExtract) {
        return `Extract ${extractablePageCount} ${extractablePageCount === 1 ? 'page' : 'pages'}`;
      } else {
        return 'Extracted';
      }
    }

    if (activeBundleType === BundleType.LIGHT) {
      if (isRemoveFromLightBundleLoading) {
        return 'Removing...';
      } else {
        return `Remove ${removablePageCount} ${removablePageCount === 1 ? 'page' : 'pages'}`;
      }
    }
  }, [
    activeBundleType,
    canExtract,
    extractablePageCount,
    removablePageCount,
    isAddToLightBundleLoading,
    isExtractToLoading,
    isRemoveFromLightBundleLoading,
  ]);

  // useEffect(() => {
  //   const handleKeyDown = (event: KeyboardEvent) => {
  //     if (event.key === '=' && activeBundleType === BundleType.FULL && canExtract && !isAddToLightBundleLoading) {
  //       event.preventDefault();
  //       handleExtract();
  //     }
  //   };
  //
  //   document.addEventListener('keydown', handleKeyDown);
  //   return () => {
  //     document.removeEventListener('keydown', handleKeyDown);
  //   };
  // }, [activeBundleType, canExtract, isAddToLightBundleLoading, handleExtract]);

  if (!isEditBundles) {
    return null;
  }

  return (
    <>
      {activeBundleType === BundleType.FULL && (
        <div
          className={classNames('relative rounded-[4px] transition-transform duration-300', {
            'scale-[1.04]': showSuccess,
          })}
        >
          {/* Animated gradient sweep overlay */}
          {isExtracting && (
            <div
              className="pointer-events-none absolute inset-0 z-10 rounded-[4px]"
              style={{
                background:
                  'linear-gradient(90deg, transparent 0%, rgba(57,163,122,0.18) 40%, rgba(57,163,122,0.35) 60%, transparent 100%)',
                backgroundSize: '200% 100%',
                animation: 'extractSweep 1.4s ease-in-out infinite',
              }}
            />
          )}
          {/* Success radial glow */}
          {showSuccess && (
            <>
              <div
                className="pointer-events-none absolute inset-[-1px] z-20 rounded-[4px]"
                style={{
                  animation: 'extractGlow 1.6s ease-out forwards',
                }}
              />
              <div
                className="pointer-events-none absolute z-20 rounded-[4px]"
                style={{
                  inset: '-1px',
                  animation: 'extractRing 1.4s ease-out forwards',
                }}
              />
            </>
          )}
          {canExtract && !isExtracting ? (
            <SplittedButton
              label={label}
              loading={false}
              loadingSplitted={false}
              onClick={handleExtractWithSuccess}
              onClickSplitted={handleClickExtractTo}
              disabled={showCategoriesPopup}
              disabledSplitted={false}
              icon="pi pi-plus !text-[12px]"
              pt={{
                ...buttonCommonPt,
                root: {
                  className: classNames(
                    buttonCommonPt.root.className,
                    'transition-all duration-500 ease-in-out',
                    { 'cursor-not-allowed': showCategoriesPopup },
                    showCategoriesPopup ? '!hidden' : '',
                  ),
                },
              }}
              ptSplitted={{
                root: {
                  className: classNames(
                    'max-w-8 min-w-8 w-8 !h-[32px] items-center',
                    'transition-all duration-500 ease-in-out',
                    showCategoriesPopup ? '!hidden' : '',
                  ),
                },
              }}
            >
              {showCategoriesPopup && (
                <ExtractToPopup
                  categoriesVirtualList={categoriesVirtualList}
                  showNewCategoryAutocomplete={showNewCategoryAutocomplete}
                  onClose={handleClickExtractTo}
                  onExtract={(category) => {
                    handleClickSubmitNewCategory(category);
                    setShowCategoriesPopup(false);
                  }}
                  onNewCategoryClick={handleClickNewCategory}
                  onNewCategoryCreate={handleNewCategoryCreated}
                  onNewCategoryCancel={handleClickCancelNewCategory}
                  setExtractedDate={setExtractedDate}
                />
              )}
            </SplittedButton>
          ) : (
            <Button
              label={label}
              loading={isExtracting}
              disabled={!canExtract}
              icon={
                !isExtracting && !canExtract ? (
                  <CheckCircleIcon className="[&>path]:fill-fletchers-green-500 mr-1" />
                ) : undefined
              }
              pt={{
                ...buttonCommonPt,
                root: {
                  className: classNames(
                    buttonCommonPt.root.className,
                    'transition-all duration-500 ease-in-out',
                    'disabled:bg-white disabled:border-white disabled:text-fletchers-green-500 disabled:shadow-lg',
                  ),
                },
                loadingIcon: {
                  className: 'animate-spin mr-1',
                },
              }}
            />
          )}
        </div>
      )}

      {activeBundleType === BundleType.LIGHT && (
        <Button
          label={label}
          disabled={isRemoveFromLightBundleLoading || isBundleLoading}
          loading={isRemoveFromLightBundleLoading}
          onClick={handleRemove}
          icon="pi pi-times"
          severity="danger"
          pt={{
            ...buttonCommonPt,
            root: {
              className: classNames(buttonCommonPt.root.className, {
                'disabled:bg-white disabled:border-white disabled:text-fletchers-red-500 disabled:shadow-lg':
                  isRemoveFromLightBundleLoading,
              }),
            },
          }}
        />
      )}

      <Toast ref={toastRef} />
    </>
  );
};
